import React, { useEffect, useState } from "react";
import { View, Text, Pressable, Modal } from "react-native";
import { ArrowLeft, CloseCircle } from "iconsax-react-nativejs";
import { useTranslation } from "react-i18next";
import ScButton from "../../ui-kit/button";
import ScRadioOption from "../../ui-kit/radio-option";
import ItemSelectorList from "../../action-modals/item-selection/item-selector-list";
import { SelectableItem } from "../../action-modals/item-selection/item-row";
import {
  useGetGroups,
  useGetUsers,
} from "@/src/services/api/groups/groups.hooks";
import { VisibilityType } from "@/src/hooks/features/posts/use-post-creation-modal";
import { useAuth } from "@/src/contexts/auth.context";
import { VISIBILITY_OPTIONS } from "@/src/constants/posts";
import { useTheme } from "@/src/styles/theme-provider";

type VisibilityModalProps = Readonly<{
  visible: boolean;
  onClose: () => void;
  selectedGroupIds: string[];
  selectedUserIds: string[];
  setSelectedGroupIds: ([]) => void;
  setSelectedUserIds: ([]) => void;
  toggleGroup: (item: SelectableItem) => void;
  toggleUser: (item: SelectableItem) => void;
  visibility: string;
  onVisibilityChange: (visibility: VisibilityType) => void;
  groupId?: boolean;
}>;

type Tabs = "default" | "group" | "specific";

const VisibilityModal = ({
  visible,
  onClose,
  selectedGroupIds,
  selectedUserIds,
  setSelectedGroupIds,
  setSelectedUserIds,
  toggleGroup,
  toggleUser,
  visibility,
  onVisibilityChange,
  groupId
}: VisibilityModalProps) => {
  const { t } = useTranslation();
  const { varStyle } = useTheme();
  const [tab, setTab] = useState<Tabs>("default");

  const {themeVars} = useTheme();

  const filterFn = (item: SelectableItem, search: string) => {
    const lower = search.toLowerCase();
    return (
      item.displayName?.toLowerCase().includes(lower) ??
      item.userName?.toLowerCase().includes(lower) ??
      false
    );
  };

  const {
    data: groupsData,
    fetchNextPage: fetchNextGroupsPage,
    hasNextPage: groupsHasNextPage,
    isFetchingNextPage: groupsIsFetchingNextPage,
  } = useGetGroups();
  const {
    data: usersData,
    fetchNextPage: fetchNextUsersPage,
    hasNextPage: usersHasNextPage,
    isFetchingNextPage: usersIsFetchingNextPage,
  } = useGetUsers();

  const groups: SelectableItem[] = (
    groupsData?.pages.flatMap((page) => page?.getgroups?.items ?? []) ?? []
  ).map((group) => ({
    itemId: group.ItemId,
    displayName: group.name,
    userName: undefined,
    profileImageUrl: group.coverImageUrl,
  }));
  const users: SelectableItem[] = (
    usersData?.pages.flatMap((page) => page?.users ?? []) ?? []
  ).map((user) => ({
    itemId: user.itemId,
    displayName: user.displayName,
    userName: user.userName,
    profileImageUrl: user.profileImageUrl,
  }));

  const renderVisibilityOptions = () => {
    switch (tab) {
      case "group":
        return (
          <ItemSelectorList
            items={groups}
            selectedIds={selectedGroupIds}
            searchPlaceholderKey="LABEL_SEARCH_GROUPS"
            filterFn={filterFn}
            onToggle={(item) => toggleGroup(item)}
            onClear={() => setSelectedGroupIds([])}
            onEndReached={() => {
              if (groupsHasNextPage && !groupsIsFetchingNextPage)
                fetchNextGroupsPage();
            }}
            isFetchingMore={groupsIsFetchingNextPage}
          />
        );

      case "specific":
        return (
          <ItemSelectorList
            items={users}
            selectedIds={selectedUserIds}
            searchPlaceholderKey="LABEL_SEARCH_USERS"
            filterFn={filterFn}
            onToggle={(item) => toggleUser(item)}
            onClear={() => setSelectedUserIds([])}
            onEndReached={() => {
              if (usersHasNextPage && !usersIsFetchingNextPage)
                fetchNextUsersPage();
            }}
            isFetchingMore={usersIsFetchingNextPage}
          />
        );
      default:
        return VISIBILITY_OPTIONS.map((option) => (
          <ScRadioOption
            key={option.key}
            selected={visibility === option.key}
            onSelect={() => {
              onVisibilityChange(option.key as VisibilityType);
              setTab(option.key as Tabs);
            }}
            label={option.label}
            description={option.description}
          />
        ));
    }
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      {/* Dark overlay */}
      <View className="flex-1 items-center justify-center px-4" style={themeVars as any}>
        {/* Sheet */}
        <View
          className="w-full max-w-md bg-chalk rounded-2xl overflow-hidden"
          style={{ maxHeight: "80%" }}
        >
          {/* Header */}
          <View className="flex-row justify-between items-center gap-3 px-5 pt-5 pb-4 border-b border-ash-3">
            <View className="flex-row gap-2">
              <Pressable
                hitSlop={8}
                className="hover:bg-ash-3 hover:rounded-full"
                onPress={() => {
                  setTab("default");
                }}
              >
                {(tab === "group" || tab === "specific") && (
                  <ArrowLeft color="grey" />
                )}
              </Pressable>
              <Text className="text-base text-onyx font-semibold">
                {t("LABEL_SELECT_VISIBILITY")}
              </Text>
            </View>
            <Pressable
              hitSlop={8}
              className="p-1.5 hover:bg-ash-3 hover:rounded-full"
              onPress={() => {
                setTab("default");
                onClose();
              }}
            >
              <CloseCircle size={20} color="grey" />
            </Pressable>
          </View>
          {renderVisibilityOptions()}
          {/* Footer actions */}
          <View className="flex-row items-center justify-end gap-3 px-5 py-4 border-t border-ash-3">
            <ScButton
              text="BTN_CANCEL"
              mode="outlined"
              onPress={() => {
                setTab("default");
                onClose();
              }}
            />

            <ScButton
              text="BTN_CONFIRM"
              onPress={() => {
                setTab("default");
                onClose();
              }}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
};

export default VisibilityModal;
